import { z } from "zod";

export const PRIMARY_TEXT_MODEL = "gpt-5.6-sol" as const;
export const FAST_TEXT_MODEL = PRIMARY_TEXT_MODEL;
export const IMAGE_MODEL = "gpt-image-2" as const;

export const TEXT_MODEL_OPTIONS = [PRIMARY_TEXT_MODEL] as const;
export const IMAGE_MODEL_OPTIONS = [IMAGE_MODEL] as const;
export const REASONING_EFFORT_OPTIONS = ["none", "low", "medium", "high", "xhigh", "max"] as const;
export const SUPPORTED_REASONING_EFFORT_OPTIONS = REASONING_EFFORT_OPTIONS;
export const IMAGE_SIZE_OPTIONS = [
  "auto",
  "1024x1024",
  "1536x1024",
  "1024x1536",
  "2048x1152",
  "2048x2048",
  "3840x2160",
  "2160x3840",
] as const;
export const DEFAULT_IMAGE_SIZE = "2048x1152" as const;

export const generationPreferencesSchema = z.object({
  textModel: z.enum(TEXT_MODEL_OPTIONS).default(PRIMARY_TEXT_MODEL),
  reasoningEffort: z.enum(REASONING_EFFORT_OPTIONS).default("low"),
  imageModel: z.enum(IMAGE_MODEL_OPTIONS).default(IMAGE_MODEL),
  imageSize: z.enum(IMAGE_SIZE_OPTIONS).default(DEFAULT_IMAGE_SIZE),
}).strict();

export type TextModel = (typeof TEXT_MODEL_OPTIONS)[number];
export type ImageModel = (typeof IMAGE_MODEL_OPTIONS)[number];
export type ReasoningEffort = (typeof REASONING_EFFORT_OPTIONS)[number];
export type ImageSize = (typeof IMAGE_SIZE_OPTIONS)[number];
export type GenerationPreferences = z.infer<typeof generationPreferencesSchema>;

const LEGACY_TEXT_MODELS = new Set([
  "deepseek-v4-flash-vision-exp",
  "grok-4.6", "grok-4.5", "grok-chat-fast", "grok-composer-2.5-fast",
  "gpt-5.4-mini",
]);
const LEGACY_IMAGE_MODELS = new Set([
  "grok-imagine-image-2.0",
  "grok-imagine-image", "grok-imagine-image-lite",
]);

// Decode-only migration for already persisted local projects. Active request
// schemas and public catalogs still reject every non-current model.
export function migratePersistedGenerationPreferences(value: unknown): unknown {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  const input = value as Record<string, unknown>;
  const textModel = LEGACY_TEXT_MODELS.has(String(input.textModel)) ? PRIMARY_TEXT_MODEL : input.textModel;
  const imageModel = LEGACY_IMAGE_MODELS.has(String(input.imageModel)) ? IMAGE_MODEL : input.imageModel;
  const reasoningEffort = REASONING_EFFORT_OPTIONS.includes(input.reasoningEffort as ReasoningEffort)
    ? input.reasoningEffort
    : "low";
  const imageSize = IMAGE_SIZE_OPTIONS.includes(input.imageSize as ImageSize)
    ? input.imageSize
    : DEFAULT_IMAGE_SIZE;
  if (textModel === input.textModel && imageModel === input.imageModel && reasoningEffort === input.reasoningEffort && imageSize === input.imageSize) return value;
  return { ...input, textModel, imageModel, reasoningEffort, imageSize };
}

export function reasoningEffortsForModel(_model: TextModel): readonly ReasoningEffort[] {
  return SUPPORTED_REASONING_EFFORT_OPTIONS;
}

export const publicModelCatalogSchema = z.object({
  textModels: z.array(z.enum(TEXT_MODEL_OPTIONS)).min(1),
  imageModels: z.array(z.enum(IMAGE_MODEL_OPTIONS)).min(1),
  reasoningEfforts: z.array(z.enum(REASONING_EFFORT_OPTIONS)).min(1),
  imageSizes: z.array(z.enum(IMAGE_SIZE_OPTIONS)).min(1),
  defaults: generationPreferencesSchema,
}).strict().refine((catalog) =>
  catalog.textModels.includes(catalog.defaults.textModel) &&
  catalog.imageModels.includes(catalog.defaults.imageModel) &&
  catalog.reasoningEfforts.includes(catalog.defaults.reasoningEffort) &&
  catalog.imageSizes.includes(catalog.defaults.imageSize), "Default model preferences must be available.");
export type PublicModelCatalog = z.infer<typeof publicModelCatalogSchema>;
